import React, { useEffect, useState } from 'react';
import { Row, Col, Badge, Button, Table, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaCreditCard, FaExternalLinkAlt, FaFileInvoice, FaCalendarAlt, FaArrowUp, FaEnvelopeOpenText } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchCurrentSubscription,
  fetchPayments,
  createPortalSession,
  createCheckoutSession,
} from '../../features/subscriptions/subscriptionsSlice';

const STATUS_VARIANTS: Record<string, string> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  canceled: 'danger',
  incomplete: 'secondary',
  unpaid: 'danger',
  pending_payment: 'warning',
};

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentSubscription, payments, paymentsPagination, isLoading, error } = useAppSelector(
    (state) => state.subscriptions,
  );
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchCurrentSubscription());
    dispatch(fetchPayments({ page: 1, page_size: 10 }));
  }, [dispatch]);

  const handleManageBilling = async () => {
    const result = await dispatch(createPortalSession());
    if (createPortalSession.fulfilled.match(result)) {
      window.location.href = result.payload.portal_url;
    }
  };

  const handleCompletePayment = async () => {
    if (!currentSubscription?.plan_id) return;
    setCheckoutLoading(true);
    try {
      const result = await dispatch(createCheckoutSession(currentSubscription.plan_id));
      if (createCheckoutSession.fulfilled.match(result)) {
        window.location.href = result.payload.checkout_url;
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: 20,
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid var(--border-color)',
    background: 'var(--bg-secondary)',
  };

  if (isLoading && !currentSubscription) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner animation="border" style={{ color: 'var(--brand-primary)' }} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ padding: '1.5rem' }}>
      <h3 className="fw-bold mb-4" style={{ color: 'var(--text-primary)' }}>
        {t('subscription_management', 'Subscription')}
      </h3>

      {error && currentSubscription && <Alert variant="warning" className="mb-4">{error}</Alert>}

      {currentSubscription && currentSubscription.status === 'pending_payment' ? (
        <div style={cardStyle} className="mb-4">
          <div style={{ padding: '2rem' }}>
            <div className="d-flex align-items-start gap-3 mb-3">
              <div
                style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FaEnvelopeOpenText size={24} style={{ color: '#f59e0b' }} />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className="fw-bold mb-0" style={{ color: 'var(--text-primary)' }}>
                    {currentSubscription.plan?.name || t('subscription', 'Subscription')}
                  </h5>
                  <Badge bg="warning" text="dark">
                    {t('status_pending_payment', 'Pending Payment')}
                  </Badge>
                </div>
                <p className="mb-0" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  {t('pending_payment_description', 'A subscription has been assigned to you. Complete the payment to activate it.')}
                </p>
              </div>
            </div>

            {currentSubscription.plan && (
              <div
                className="p-3 mb-3"
                style={{
                  background: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)',
                  borderRadius: 12,
                  border: '1px solid var(--border-color)',
                }}
              >
                <Row className="g-2 small">
                  <Col sm={4}>
                    <strong style={{ color: 'var(--text-secondary)' }}>{t('plan', 'Plan')}:</strong>{' '}
                    <span style={{ color: 'var(--text-primary)' }}>{currentSubscription.plan.name}</span>
                  </Col>
                  <Col sm={4}>
                    <strong style={{ color: 'var(--text-secondary)' }}>{t('price', 'Price')}:</strong>{' '}
                    <span style={{ color: 'var(--text-primary)' }}>
                      {formatPrice(currentSubscription.plan.price, currentSubscription.plan.currency)}
                      /{t(`interval_${currentSubscription.plan.interval}`, currentSubscription.plan.interval)}
                    </span>
                  </Col>
                  {currentSubscription.plan.trial_days > 0 && (
                    <Col sm={4}>
                      <strong style={{ color: 'var(--text-secondary)' }}>{t('trial', 'Trial')}:</strong>{' '}
                      <span style={{ color: 'var(--text-primary)' }}>
                        {currentSubscription.plan.trial_days} {t('days', 'days')}
                      </span>
                    </Col>
                  )}
                </Row>
              </div>
            )}

            <Button
              variant="primary"
              className="btn-modern btn-primary"
              onClick={handleCompletePayment}
              disabled={checkoutLoading}
              style={{ minWidth: 200 }}
            >
              {checkoutLoading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <FaCreditCard className="me-2" />
              )}
              {t('complete_payment', 'Complete Payment')}
            </Button>
          </div>
        </div>
      ) : currentSubscription ? (
        <div style={cardStyle} className="mb-4">
          <div style={{ padding: '1.5rem 2rem' }}>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
                  <h5 className="fw-bold mb-0" style={{ color: 'var(--text-primary)' }}>
                    {currentSubscription.plan?.name || t('subscription', 'Subscription')}
                  </h5>
                  <Badge bg={STATUS_VARIANTS[currentSubscription.status] || 'secondary'}>
                    {t(`status_${currentSubscription.status}`, currentSubscription.status)}
                  </Badge>
                  {currentSubscription.cancel_at_period_end && (
                    <Badge bg="warning" text="dark">
                      {t('cancels_at_period_end', 'Cancels at period end')}
                    </Badge>
                  )}
                </div>

                <Row className="g-3 small" style={{ color: 'var(--text-muted)' }}>
                  {currentSubscription.plan && (
                    <Col sm={4}>
                      <strong style={{ color: 'var(--text-secondary)' }}>{t('price', 'Price')}:</strong>{' '}
                      {formatPrice(currentSubscription.plan.price, currentSubscription.plan.currency)}
                      /{t(`interval_${currentSubscription.plan.interval}`, currentSubscription.plan.interval)}
                    </Col>
                  )}
                  <Col sm={4}>
                    <FaCalendarAlt className="me-1" />
                    <strong style={{ color: 'var(--text-secondary)' }}>{t('current_period', 'Period')}:</strong>{' '}
                    {formatDate(currentSubscription.current_period_start)} – {formatDate(currentSubscription.current_period_end)}
                  </Col>
                  {currentSubscription.trial_end && (
                    <Col sm={4}>
                      <strong style={{ color: 'var(--text-secondary)' }}>{t('trial_ends', 'Trial ends')}:</strong>{' '}
                      {formatDate(currentSubscription.trial_end)}
                    </Col>
                  )}
                </Row>
              </Col>
              <Col md={4} className="text-md-end mt-3 mt-md-0">
                <div className="d-flex gap-2 justify-content-md-end flex-wrap">
                  <Button variant="primary" className="btn-modern btn-primary" onClick={handleManageBilling} disabled={isLoading}>
                    <FaCreditCard className="me-1" /> {t('manage_billing', 'Manage Billing')}
                  </Button>
                  <Button variant="outline-secondary" className="btn-modern btn-secondary" onClick={() => navigate('/pricing')}>
                    <FaArrowUp className="me-1" /> {t('change_plan', 'Change Plan')}
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      ) : (
        <div style={cardStyle} className="mb-4">
          <div className="text-center" style={{ padding: '3rem 2rem' }}>
            <p style={{ color: 'var(--text-muted)' }} className="mb-3">
              {t('no_subscription', 'You don\'t have an active subscription yet.')}
            </p>
            <Button variant="primary" className="btn-modern btn-primary" onClick={() => navigate('/pricing')}>
              {t('view_plans', 'View Plans')}
            </Button>
          </div>
        </div>
      )}

      <div style={cardStyle}>
        <div style={headerStyle} className="d-flex align-items-center">
          <FaFileInvoice className="me-2" style={{ color: 'var(--text-muted)' }} />
          <h6 className="mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
            {t('payment_history', 'Payment History')}
          </h6>
        </div>
        <div style={{ padding: 0 }}>
          {payments.length === 0 ? (
            <div className="text-center py-5" style={{ color: 'var(--text-muted)' }}>
              {t('no_payments', 'No payments yet')}
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>{t('date', 'Date')}</th>
                  <th style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>{t('amount', 'Amount')}</th>
                  <th style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>{t('status', 'Status')}</th>
                  <th style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>{t('invoice', 'Invoice')}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                      {formatDate(payment.paid_at || payment.created_at)}
                    </td>
                    <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)', fontWeight: 600 }}>
                      {formatPrice(payment.amount, payment.currency)}
                    </td>
                    <td style={{ borderColor: 'var(--border-color)' }}>
                      <Badge bg={payment.status === 'paid' ? 'success' : payment.status === 'failed' ? 'danger' : 'secondary'}>
                        {t(`payment_status_${payment.status}`, payment.status)}
                      </Badge>
                    </td>
                    <td style={{ borderColor: 'var(--border-color)' }}>
                      {payment.invoice_url && (
                        <a href={payment.invoice_url} target="_blank" rel="noopener noreferrer"
                          className="text-decoration-none" style={{ color: 'var(--brand-primary)' }}>
                          <FaExternalLinkAlt className="me-1" /> {t('view_invoice', 'View')}
                        </a>
                      )}
                      {payment.invoice_pdf && (
                        <a href={payment.invoice_pdf} target="_blank" rel="noopener noreferrer"
                          className="ms-3 text-decoration-none" style={{ color: 'var(--brand-primary)' }}>
                          {t('download_pdf', 'PDF')}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
